import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Dialog } from '@mui/material'
import { Link } from 'react-router-dom'
import Icon from '../../ui/Icon/Icon'
import { AuthContext } from '../../App'
import { firebase } from '../../config/firebase'
import { canAccessOfflineRegistration } from '../../config/offlineRegistrationAccess'
import { createRegistrationRequestId, offlineRegistration } from '../../api/customers/offlineRegistration'
import AuthPhoneField from './storefront/components/AuthPhoneField'
import AuthTextField from './storefront/components/AuthTextField'
import OtpCodeInput from './storefront/components/OtpCodeInput'
import {
  AUTH_ERROR_CODES,
  AUTH_NAME_MAX_LENGTH,
  AUTH_OTP_LENGTH,
  AUTH_RESEND_MS,
  BACKEND_ERROR_CODE_MAP,
} from './storefront/authRegistrationConstants'
import {
  buildUaPhoneValue,
  extractUaNationalDigits,
  maskPhoneE164,
  normalizeUaPhoneToE164,
} from './storefront/validation/phoneValidation'
import {
  validateRegistrationEmail,
  validateRegistrationLastName,
  validateRegistrationName,
} from './storefront/validation/profileValidation'
import formStyles from './storefront/AuthRegistration.module.scss'
import ameLogo from './assets/ame-logo.svg'
import './OfflineRegistration.scss'

export function OfflineBrand() {
  return (
    <Link className="offline-brand" to="/offline-registration" aria-label="Ame Cosmetics">
      <img src={ameLogo} alt="Ame Cosmetics" width="67" height="52" />
    </Link>
  )
}

const formatRegistrationDate = (value) =>
  value
    ? new Intl.DateTimeFormat('uk-UA', {
        timeZone: 'Europe/Kyiv',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(value))
    : '—'

const registrationChannelLabel = (channel) =>
  ({ storefront: 'Сайт', offline_store: 'Офлайн-магазин' }[channel] || 'Не визначено')

const fullName = (customer) =>
  [customer?.firstName, customer?.lastName].filter(Boolean).join(' ') || 'Профіль не завершено'

const formatMmSs = (seconds) =>
  `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`

const errorMessage = (error) => {
  if (String(error?.code).includes('unavailable') || String(error?.code).includes('deadline')) {
    return 'Немає зв’язку. Перевірте інтернет і спробуйте ще раз.'
  }
  if (String(error?.code).includes('not-found')) return 'Сервіс реєстрації поки недоступний. Спробуйте пізніше.'
  return error?.message && error.message !== 'internal' ? error.message : 'Не вдалося виконати дію. Спробуйте ще раз.'
}

function OfflineDialogHeading({ id, title, onClose, busy = false }) {
  return (
    <div className="offline-dialog-heading">
      <h2 id={id}>{title}</h2>
      <button className="offline-close" type="button" aria-label="Закрити" disabled={busy} onClick={onClose}>
        <Icon name="times" weight="light" size={14} />
      </button>
    </div>
  )
}

function CustomerSummary({ customer }) {
  return (
    <dl className="offline-customer-summary">
      <div>
        <dt>Клієнт</dt>
        <dd>{fullName(customer)}</dd>
      </div>
      <div>
        <dt>Телефон</dt>
        <dd>{customer?.phoneE164 || '—'}</dd>
      </div>
      <div>
        <dt>Email</dt>
        <dd>{customer?.email || '—'}</dd>
      </div>
    </dl>
  )
}

export function RegistrationDialog({ open, initialPhone = '', onClose, onRegistered, api = offlineRegistration }) {
  const [step, setStep] = useState('phone')
  const [nationalDigits, setNationalDigits] = useState(() => extractUaNationalDigits(initialPhone))
  const [phoneBlurred, setPhoneBlurred] = useState(false)
  const [maskedRecipient, setMaskedRecipient] = useState('')
  const [fieldErrors, setFieldErrors] = useState({})
  const [code, setCode] = useState('')
  const [profile, setProfile] = useState({ firstName: '', lastName: '', email: '' })
  const [verificationToken, setVerificationToken] = useState(null)
  const [customer, setCustomer] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [resendAt, setResendAt] = useState(0)
  const [clock, setClock] = useState(Date.now())
  const inFlight = useRef(false)
  const requestId = useRef(null)
  const submittedCode = useRef(null)
  const phoneValue = buildUaPhoneValue(nationalDigits)
  const phone = phoneValue.e164 || ''
  const phoneError =
    fieldErrors.phone ||
    (step === 'phone' && error) ||
    ((phoneValue.isComplete && !phoneValue.isValid) ||
    (phoneBlurred && nationalDigits.length > 0 && !phoneValue.isComplete)
      ? 'Введіть коректний номер телефону'
      : null)

  const reset = useCallback((nextPhone = '') => {
    setStep('phone')
    setNationalDigits(extractUaNationalDigits(nextPhone))
    setPhoneBlurred(false)
    setMaskedRecipient('')
    setFieldErrors({})
    setCode('')
    setProfile({ firstName: '', lastName: '', email: '' })
    setVerificationToken(null)
    setCustomer(null)
    setError('')
    setResendAt(0)
    submittedCode.current = null
    requestId.current = createRegistrationRequestId()
  }, [])

  useEffect(() => {
    if (open) reset(initialPhone)
  }, [open, initialPhone, reset])
  useEffect(() => {
    if (!open || step !== 'otp') return undefined
    const timer = window.setInterval(() => setClock(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [open, step])

  const perform = useCallback(async (task) => {
    if (inFlight.current) return
    inFlight.current = true
    setBusy(true)
    setError('')
    try {
      await task()
    } catch (failure) {
      const invalidField = {
        [AUTH_ERROR_CODES.INVALID_PHONE]: 'phone',
        [AUTH_ERROR_CODES.INVALID_NAME]: 'firstName',
        [AUTH_ERROR_CODES.INVALID_LAST_NAME]: 'lastName',
        [AUTH_ERROR_CODES.INVALID_EMAIL]: 'email',
      }[BACKEND_ERROR_CODE_MAP[failure?.details?.errorCode]]
      if (invalidField) setFieldErrors({ [invalidField]: errorMessage(failure) })
      else setError(errorMessage(failure))
      if (failure?.details?.retryAfterSec) {
        setResendAt(Date.now() + failure.details.retryAfterSec * 1000)
      }
    } finally {
      inFlight.current = false
      setBusy(false)
    }
  }, [])

  const acceptResult = useCallback(
    (result) => {
      if (result.outcome === 'profile_required') {
        setVerificationToken(result.verificationToken)
        setStep('profile')
      } else if (result.outcome === 'registered' || result.outcome === 'already_registered') {
        setCustomer(result.customer)
        setStep(result.outcome === 'registered' ? 'success' : 'existing')
        if (result.outcome === 'registered') onRegistered()
      } else {
        throw new Error('Не вдалося завершити реєстрацію. Спробуйте ще раз.')
      }
    },
    [onRegistered]
  )

  const requestOtp = () => {
    const normalized = normalizeUaPhoneToE164(nationalDigits)
    if (!normalized.ok) {
      setFieldErrors({
        phone:
          nationalDigits.length > 0 && nationalDigits.length < 9
            ? `Введіть коректний номер телефону (${nationalDigits.length}/9)`
            : normalized.message,
      })
      return
    }
    setFieldErrors({})
    return perform(async () => {
      const result = await api({
        action: 'requestOtp',
        phone: normalized.e164,
        clientRequestId: createRegistrationRequestId(),
      })
      if (result.outcome === 'already_registered') {
        acceptResult(result)
        return
      }
      setCode('')
      submittedCode.current = null
      setMaskedRecipient(result.maskedRecipient || maskPhoneE164(normalized.e164))
      setResendAt(Date.now() + AUTH_RESEND_MS)
      setClock(Date.now())
      setStep('otp')
    })
  }

  const verifyOtp = useCallback(
    () =>
      perform(async () => {
        const result = await api({ action: 'verifyOtp', phone, code, clientRequestId: requestId.current })
        acceptResult(result)
      }),
    [api, phone, code, perform, acceptResult]
  )

  useEffect(() => {
    if (!open || step !== 'otp' || busy) return
    if (code.length !== AUTH_OTP_LENGTH) {
      submittedCode.current = null
      return
    }
    if (submittedCode.current === code) return
    submittedCode.current = code
    void verifyOtp()
  }, [open, step, busy, code, verifyOtp])

  const complete = () => {
    const firstName = validateRegistrationName(profile.firstName)
    const lastName = validateRegistrationLastName(profile.lastName)
    const email = validateRegistrationEmail(profile.email)
    const invalid = [
      ['firstName', firstName],
      ['lastName', lastName],
      ['email', email],
    ].find(([, result]) => !result.ok)
    if (invalid) {
      setError('')
      setFieldErrors({ [invalid[0]]: invalid[1].message })
      return
    }
    setFieldErrors({})
    return perform(async () => {
      acceptResult(
        await api({
          action: 'complete',
          verificationToken,
          clientRequestId: requestId.current,
          profile: {
            firstName: firstName.name,
            lastName: lastName.lastName,
            ...(email.email ? { email: email.email } : {}),
          },
        })
      )
    })
  }

  const secondsLeft = Math.max(0, Math.ceil((resendAt - clock) / 1000))
  const title = {
    phone: 'Реєстрація клієнта',
    otp: 'Підтвердження номера',
    profile: 'Дані клієнта',
    success: 'Клієнта зареєстровано',
    existing: 'Клієнт уже зареєстрований',
  }[step]

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!inFlight.current) onClose()
      }}
      fullWidth
      maxWidth={false}
      classes={{ root: 'offline-dialog-root' }}
      aria-labelledby="offline-dialog-title"
      PaperProps={{ className: 'offline-dialog offline-registration-dialog' }}
    >
      <OfflineDialogHeading id="offline-dialog-title" title={title} busy={busy} onClose={onClose} />
      {!['success', 'existing'].includes(step) && (
        <ol className="offline-steps" aria-label="Кроки реєстрації">
          {['Телефон', 'SMS-код', 'Дані'].map((label, index) => (
            <li
              key={label}
              className={index < ['phone', 'otp', 'profile'].indexOf(step) ? 'offline-step-complete' : ''}
              aria-current={['phone', 'otp', 'profile'][index] === step ? 'step' : undefined}
            >
              <span className="offline-step-content">
                <span className="offline-step-marker">{index + 1}</span>
                {label}
              </span>
            </li>
          ))}
        </ol>
      )}
      {error && !['phone', 'otp'].includes(step) && (
        <div className="offline-error" role="alert">
          {error}
        </div>
      )}
      {step === 'phone' && (
        <form
          className={formStyles.screen}
          noValidate
          onSubmit={(event) => {
            event.preventDefault()
            void requestOtp()
          }}
        >
          <p className="offline-hint">Спочатку перевіримо, чи є клієнт у базі. Для нового клієнта надішлемо SMS-код.</p>
          <AuthPhoneField
            id="offline-registration-phone"
            label="Номер телефону"
            nationalDigits={nationalDigits}
            onChange={(nextPhone) => {
              setNationalDigits(nextPhone.nationalDigits)
              setFieldErrors({})
              setError('')
            }}
            onBlurValidate={() => setPhoneBlurred(true)}
            error={phoneError}
            disabled={busy}
            autoFocus
          />
          <button className="offline-button offline-button-primary" type="submit" disabled={busy}>
            {busy ? 'Перевіряємо…' : 'Продовжити'}
          </button>
        </form>
      )}
      {step === 'otp' && (
        <form
          className={formStyles.screen}
          noValidate
          onSubmit={(event) => {
            event.preventDefault()
            if (code.length === AUTH_OTP_LENGTH) void verifyOtp()
          }}
        >
          <p className="offline-hint">
            SMS-код надіслано на <strong>{maskedRecipient}</strong>. Попросіть клієнта назвати 4 цифри.
          </p>
          <button
            className="offline-text-button"
            disabled={busy}
            type="button"
            onClick={() => {
              setStep('phone')
              setCode('')
              setError('')
              setFieldErrors({})
              requestId.current = createRegistrationRequestId()
            }}
          >
            Змінити номер
          </button>
          <div className={formStyles.otpBlock}>
            <OtpCodeInput
              value={code}
              onChange={(nextCode) => {
                setCode(nextCode)
                setError('')
              }}
              disabled={busy}
              hasError={Boolean(error)}
              autoFocus
            />
            {error && (
              <p className={formStyles.fieldError} role="alert">
                {error}
              </p>
            )}
          </div>
          <button
            className="offline-button offline-button-primary"
            type="submit"
            disabled={busy || code.length !== AUTH_OTP_LENGTH}
          >
            {busy ? 'Перевіряємо…' : 'Підтвердити'}
          </button>
          <div className={formStyles.resendRow}>
            {secondsLeft > 0 ? (
              <span className={formStyles.resendWait}>Надіслати код повторно через {formatMmSs(secondsLeft)}</span>
            ) : (
              <button className={formStyles.textAction} type="button" disabled={busy} onClick={requestOtp}>
                Надіслати код ще раз
              </button>
            )}
          </div>
        </form>
      )}
      {step === 'profile' && (
        <>
          <div className="offline-profile-info">
            <p className="offline-hint">
              Номер телефону <strong>+380 {phoneValue.display}</strong> підтверджено.
            </p>
            <p className="offline-hint">
              Вкажіть ім’я та прізвище клієнта, щоб завершити реєстрацію. Email можна додати за бажанням.
            </p>
          </div>
          <form
            className={formStyles.screen}
            noValidate
            onSubmit={(event) => {
              event.preventDefault()
              void complete()
            }}
          >
            <div className={formStyles.nameRow}>
              {[
                ['firstName', 'Ім’я', 'given-name'],
                ['lastName', 'Прізвище', 'family-name'],
              ].map(([key, label, autoComplete], index) => (
                <AuthTextField
                  key={key}
                  id={`offline-registration-${key}`}
                  label={label}
                  value={profile[key]}
                  onChange={(value) => {
                    setProfile((previous) => ({ ...previous, [key]: value }))
                    setFieldErrors({})
                    setError('')
                  }}
                  autoComplete={autoComplete}
                  maxLength={AUTH_NAME_MAX_LENGTH}
                  error={fieldErrors[key]}
                  disabled={busy}
                  required
                  autoFocus={index === 0}
                />
              ))}
            </div>
            <AuthTextField
              id="offline-registration-email"
              label="Email (необов’язково)"
              type="email"
              value={profile.email}
              onChange={(value) => {
                setProfile((previous) => ({ ...previous, email: value }))
                setFieldErrors({})
                setError('')
              }}
              autoComplete="email"
              inputMode="email"
              error={fieldErrors.email}
              disabled={busy}
            />
            <button className="offline-button offline-button-primary" type="submit" disabled={busy}>
              {busy ? 'Зберігаємо…' : 'Завершити реєстрацію'}
            </button>
          </form>
        </>
      )}
      {['success', 'existing'].includes(step) && (
        <div className="offline-result">
          {step === 'success' && (
            <div className="offline-status" role="status">
              <span className="offline-status-icon" aria-hidden="true">
                <Icon name="check" weight="solid" size={12} />
              </span>
              <p className="offline-status-text">Профіль збережено в загальній базі клієнтів Ame Cosmetics.</p>
            </div>
          )}
          <CustomerSummary customer={customer} />
          <button className="offline-button offline-button-primary" type="button" onClick={() => reset()}>
            Зареєструвати наступного
          </button>
          <button className="offline-text-button offline-resend" type="button" onClick={onClose}>
            Готово
          </button>
        </div>
      )}
    </Dialog>
  )
}

function CustomersTable({ items = [], busy = false, showSeller = false, emptyIcon, emptyTitle, emptyText }) {
  return (
    <>
      <div className="offline-table-scroll" aria-busy={busy}>
        <table className="offline-table">
          <thead>
            <tr>
              <th>Клієнт</th>
              <th>Телефон</th>
              <th>Email</th>
              <th>Дата реєстрації</th>
              <th>Джерело реєстрації</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id}>
                <td data-label="Клієнт">
                  <strong>{fullName(item)}</strong>
                  {showSeller && item.registeredByEmail ? <small>{item.registeredByEmail}</small> : null}
                </td>
                <td data-label="Телефон">{item.phoneE164 || '—'}</td>
                <td data-label="Email">{item.email || '—'}</td>
                <td data-label="Дата реєстрації">
                  {formatRegistrationDate(item.registeredAt || item.createdAt)}
                </td>
                <td data-label="Джерело реєстрації">{registrationChannelLabel(item.registrationChannel)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!items.length ? (
        <div className="offline-empty">
          <div aria-hidden="true">
            <Icon name={emptyIcon} weight="light" size={22} />
          </div>
          <h3>{emptyTitle}</h3>
          <p>{emptyText}</p>
        </div>
      ) : null}
    </>
  )
}

function OfflineRegistration({ api = offlineRegistration }) {
  const { user } = useContext(AuthContext)
  const [today, setToday] = useState({ items: [], total: 0, completedProfiles: 0 })
  const [todayLoading, setTodayLoading] = useState(true)
  const [todayLoaded, setTodayLoaded] = useState(false)
  const [query, setQuery] = useState('')
  const [appliedQuery, setAppliedQuery] = useState('')
  const [search, setSearch] = useState(null)
  const [searchLoading, setSearchLoading] = useState(false)
  const [todayError, setTodayError] = useState('')
  const [searchError, setSearchError] = useState('')
  const [registrationOpen, setRegistrationOpen] = useState(false)
  const [initialPhone, setInitialPhone] = useState('')
  const searchSequence = useRef(0)
  const allowed = canAccessOfflineRegistration(user)

  const loadToday = useCallback(async () => {
    try {
      const result = await api({ action: 'today' })
      setToday(result)
      setTodayLoaded(true)
      setTodayError('')
    } catch (failure) {
      setTodayError('Не вдалося завантажити сьогоднішні реєстрації. Спробуйте ще раз.')
    } finally {
      setTodayLoading(false)
    }
  }, [api])

  useEffect(() => {
    document.title = 'Офлайн-реєстрація · Ame Cosmetics'
    if (!allowed) return undefined
    void loadToday()
    const timer = window.setInterval(loadToday, 60000)
    return () => window.clearInterval(timer)
  }, [allowed, loadToday])

  const findCustomers = async (offset = 0) => {
    const currentQuery = offset ? appliedQuery : query.trim()
    if (currentQuery.length < 2) return
    const sequence = ++searchSequence.current
    setSearchLoading(true)
    setSearchError('')
    try {
      const result = await api({ action: 'search', query: currentQuery, offset })
      if (sequence !== searchSequence.current) return
      setAppliedQuery(currentQuery)
      setSearch((previous) => ({ ...result, items: offset ? [...(previous?.items || []), ...result.items] : result.items }))
    } catch (failure) {
      if (sequence === searchSequence.current) setSearchError(errorMessage(failure))
    } finally {
      if (sequence === searchSequence.current) setSearchLoading(false)
    }
  }

  const openRegistration = (nextPhone = '') => {
    setInitialPhone(nextPhone)
    setRegistrationOpen(true)
  }
  const clearSearch = () => {
    searchSequence.current += 1
    setQuery('')
    setAppliedQuery('')
    setSearch(null)
    setSearchLoading(false)
    setSearchError('')
  }

  if (!allowed)
    return (
      <main className="offline-page">
        <div className="offline-card offline-denied">
          <h1>Увійдіть за email та паролем</h1>
          <p>Ця сторінка призначена для працівників Ame Cosmetics.</p>
          <button className="offline-button" onClick={() => firebase.auth().signOut()}>
            Змінити обліковий запис
          </button>
        </div>
      </main>
    )

  const showSearch = Boolean(search) || searchLoading || Boolean(searchError)
  return (
    <main className="offline-page">
      <header className="offline-header">
        <div className="offline-container offline-header-inner">
          <OfflineBrand />
          <div className="offline-account">
            <span>{user.email}</span>
            <button type="button" onClick={() => firebase.auth().signOut()}>
              Вийти
            </button>
          </div>
        </div>
      </header>
      <div className="offline-container offline-content">
        <div className="offline-intro">
          <div>
            <h1>Клієнти Ame</h1>
            <p>Знайдіть клієнта або зареєструйте нового за кілька кроків.</p>
          </div>
          <button className="offline-button offline-button-primary" type="button" onClick={() => openRegistration()}>
            <Icon name="user-plus" weight="light" size={18} />
            Зареєструвати клієнта
          </button>
        </div>
        <div className="offline-card offline-search-card">
          <form
            className="offline-search-form"
            onSubmit={(event) => {
              event.preventDefault()
              void findCustomers()
            }}
          >
            <label className="offline-field">
              Пошук у загальній базі клієнтів
              <input
                type="search"
                placeholder="Номер телефону, ім’я або прізвище"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                minLength={2}
                required
              />
            </label>
            <button className="offline-button offline-button-dark" type="submit" disabled={searchLoading}>
              <Icon name="search" weight="light" size={18} />
              {searchLoading ? 'Шукаємо…' : 'Знайти'}
            </button>
            {search && (
              <button className="offline-text-button" type="button" onClick={clearSearch}>
                Скинути
              </button>
            )}
          </form>
        </div>
        {showSearch ? (
          <section className="offline-card offline-table-card" aria-label="Результати пошуку">
            <div className="offline-list-heading">
              <h2>Результати пошуку{search ? `: ${search.total}` : ''}</h2>
            </div>
            {searchError ? (
              <div className="offline-error" role="alert">
                {searchError}
                <button className="offline-text-button" type="button" onClick={() => findCustomers()}>
                  Спробувати ще раз
                </button>
              </div>
            ) : null}
            <CustomersTable
              items={search?.items || []}
              busy={searchLoading}
              emptyIcon={searchError ? 'exclamation-circle' : 'search'}
              emptyTitle={
                searchLoading
                  ? 'Шукаємо…'
                  : searchError
                  ? 'Не вдалося отримати дані'
                  : 'Клієнтів не знайдено'
              }
              emptyText={
                searchError
                  ? 'Повторіть завантаження кнопкою «Спробувати ще раз».'
                  : searchLoading
                  ? 'Шукаємо клієнтів у загальній базі.'
                  : 'Перевірте номер або написання імені. Для нового клієнта почніть реєстрацію.'
              }
            />
            {search && search.items.length < search.total ? (
              <button
                className="offline-button offline-load-more"
                type="button"
                disabled={searchLoading}
                onClick={() => findCustomers(search.items.length)}
              >
                Показати ще
              </button>
            ) : null}
          </section>
        ) : null}
        <section className="offline-card offline-table-card" aria-label="Реєстрації сьогодні">
          <div className="offline-list-heading">
            <h2>Реєстрації сьогодні</h2>
            <div className="offline-today-count" aria-live="polite">
              <strong>{todayLoading ? '…' : todayLoaded ? today.total : '—'}</strong>
              <span>нових клієнтів сьогодні</span>
            </div>
          </div>
          {todayError ? (
            <div className="offline-error" role="alert">
              {todayError}
              <button className="offline-text-button" type="button" onClick={loadToday}>
                Спробувати ще раз
              </button>
            </div>
          ) : null}
          {today.completedProfiles > 0 ? (
            <p className="offline-completed-count">Також завершено профілів: {today.completedProfiles}</p>
          ) : null}
          <CustomersTable
            items={today.items}
            busy={todayLoading}
            showSeller
            emptyIcon={todayLoading ? 'clock' : todayError ? 'exclamation-circle' : 'users'}
            emptyTitle={
              todayLoading ? 'Завантажуємо…' : todayError ? 'Не вдалося отримати дані' : 'Перший клієнт сьогодні — попереду'
            }
            emptyText={
              todayError
                ? 'Повторіть завантаження кнопкою «Спробувати ще раз».'
                : todayLoading
                ? 'Отримуємо сьогоднішні реєстрації.'
                : 'Після успішної реєстрації клієнт з’явиться в цьому списку.'
            }
          />
        </section>
      </div>
      <RegistrationDialog
        open={registrationOpen}
        initialPhone={initialPhone}
        api={api}
        onClose={() => setRegistrationOpen(false)}
        onRegistered={loadToday}
      />
    </main>
  )
}

export default OfflineRegistration
